import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
  chakra,
} from "@chakra-ui/react";
import { KYDAdminEvent } from "@common/interfaces/KYDEvent";
import { DateTimePicker, DateTimePickerProps } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";

import {
  Controller,
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";

const KydForm = chakra(chakra.form);

const DatepickerFormInput = ({
  timezone,
  label,
  name,
  deps = [],
  validate = () => true,
  validationFailMessage,
  dateTimeProps = {},
  transform = {
    input: (val: Date) => dayjs(val).tz(timezone),
    output: (val: Dayjs) => val?.toISOString(),
  },
}: {
  timezone: string;
  name: string;
  deps?: string[];
  label: string;
  validate?: (val: Dayjs, otherValues: FieldValues) => boolean | string;
  validationFailMessage?: string;
  dateTimeProps?: DateTimePickerProps<Dayjs>;
  transform?: { input: (val: any) => any; output: (val: any) => any };
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Stack w="full" spacing={0}>
      <Text fontWeight={"medium"}>{label}</Text>
      <Controller
        name={name}
        control={control}
        rules={{
          deps,
          validate: (fieldToValidate, otherValues) => {
            try {
              if (fieldToValidate === null || fieldToValidate === undefined) {
                return `${name} is required.`;
              }

              const validationResult = validate(
                transform.input(fieldToValidate),
                otherValues
              );
              if (!validationResult) {
                return validationFailMessage || `Failed validating ${name}`;
              }

              return true;
            } catch (e) {
              return validationFailMessage || `Failed validating ${name}`;
            }
          },
        }}
        render={({ field }) => (
          <DateTimePicker
            {...field}
            value={transform.input(field.value)}
            onChange={(date) => {
              field.onChange(transform.output(date));
            }}
            onAccept={(date) => field.onChange(transform.output(date))}
            timezone={timezone}
            closeOnSelect
            {...dateTimeProps}
          />
        )}
      />
      <Box h={4}>
        {errors[name] ? (
          <Text fontSize={"sm"} color="red.300">
            {errors[name].message.toString()}
          </Text>
        ) : null}
      </Box>
    </Stack>
  );
};

const EditTimelineModal = ({
  kydEvent,
  onCancel,
  onSave,
  isOpen = false,
  isLoading,
}: {
  kydEvent?: KYDAdminEvent;
  onCancel: () => void;
  onSave: (data: any) => any;
  isOpen: boolean;
  isLoading: boolean;
}) => {
  const methods = useForm({
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange",
    /*
    For context:
    - Announce at (before or equal to on sale)
    - On sale (can be entire duration until end date)
    - Door open (has to be before or equal event start date)
    - event start date (before end date && after or equal to doors open)
    - off sale (after on sale)
    - event end date (after or equal to off sale)
    */
    defaultValues: {
      announce_at: kydEvent.announce_at
        ? dayjs(kydEvent.announce_at).tz(kydEvent.timezone)
        : null,
      on_sale_at: kydEvent.on_sale_at
        ? dayjs(kydEvent.on_sale_at).tz(kydEvent.timezone)
        : null,
      off_sale_at: kydEvent.off_sale_at
        ? dayjs(kydEvent.off_sale_at).tz(kydEvent.timezone)
        : null,
      doors_at: kydEvent.doors_at
        ? dayjs(kydEvent.doors_at).tz(kydEvent.timezone)
        : null,
      start_at: dayjs(kydEvent.start_at).tz(kydEvent.timezone),
      end_at: dayjs(kydEvent.end_at).tz(kydEvent.timezone),
    },
  });
  const {
    reset,
    formState: { isValid, isDirty: isFormDirty },
    handleSubmit,
    getValues,
  } = methods;

  const onSubmit: SubmitHandler<any> = (data) => {
    return onSave(data);
  };

  const onCloseModal = () => {
    onCancel();
    reset();
  };

  return (
    <Modal
      size={"xl"}
      isOpen={isOpen}
      onClose={onCloseModal}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <KydForm w="full" onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Edit timeline</ModalHeader>
          <ModalBody>
            <FormProvider {...methods}>
              <Stack>
                <VStack
                  direction={{ base: "column", md: "row" }}
                  w="100%"
                  alignItems={"flex-start"}
                  spacing={{ base: 8, md: 6 }}
                >
                  <Text width={"full"}>Set the timing of the event</Text>
                  <Stack w="full">
                    <DatepickerFormInput
                      dateTimeProps={{
                        disabled: isLoading,
                      }}
                      deps={["on_sale_at"]}
                      validationFailMessage="Needs to be before or equal to on sale."
                      validate={(val, otherValues) => {
                        return (
                          val.isBefore(otherValues.on_sale_at) ||
                          val.isSame(otherValues.on_sale_at)
                        );
                      }}
                      name="announce_at"
                      label="Announce on"
                      timezone={kydEvent.timezone}
                    />
                  </Stack>
                  <Stack
                    spacing={{ base: 8, md: 2 }}
                    direction={["column", "row"]}
                    w="full"
                  >
                    <DatepickerFormInput
                      dateTimeProps={{ disabled: isLoading }}
                      validationFailMessage="Needs to be after announce, and less than or equal to off sale."
                      deps={["announce_at", "off_sale_at"]}
                      validate={(val, otherValues) => {
                        return (
                          (val.isAfter(otherValues.announce_at) ||
                            val.isSame(otherValues.announce_at)) &&
                          val.isBefore(otherValues.off_sale_at)
                        );
                      }}
                      name="on_sale_at"
                      label="On sale"
                      timezone={kydEvent.timezone}
                    />
                    <DatepickerFormInput
                      dateTimeProps={{ disabled: isLoading }}
                      validationFailMessage="Needs to be after on sale, and equal to or before event end."
                      deps={["on_sale_at", "end_at"]}
                      validate={(val, otherValues) => {
                        return (
                          val.isAfter(otherValues.on_sale_at) &&
                          (val.isSame(otherValues.end_at) ||
                            val.isBefore(otherValues.end_at))
                        );
                      }}
                      name="off_sale_at"
                      label="Off sale"
                      timezone={kydEvent.timezone}
                    />
                  </Stack>
                  <Stack spacing={{ base: 8, md: 2 }} w="full">
                    <DatepickerFormInput
                      dateTimeProps={{
                        disabled: isLoading,
                        minDateTime: dayjs(
                          getValues("start_at") || kydEvent.start_at
                        )
                          .tz(kydEvent.timezone)
                          .subtract(2, "days"),
                        maxDateTime: dayjs(
                          getValues("start_at") || kydEvent.start_at
                        ).tz(kydEvent.timezone),
                        defaultValue: dayjs(
                          getValues("start_at") || kydEvent.start_at
                        ).tz(kydEvent.timezone),
                      }}
                      deps={["start_at"]}
                      validationFailMessage="Needs to be equal to or before event start."
                      validate={(val, otherValues) => {
                        return (
                          val.isBefore(otherValues.start_at) ||
                          val.isSame(otherValues.start_at)
                        );
                      }}
                      name="doors_at"
                      label="Doors open"
                      timezone={kydEvent.timezone}
                    />
                  </Stack>
                  <Stack
                    spacing={{ base: 8, md: 2 }}
                    direction={["column", "row"]}
                    w="full"
                  >
                    <DatepickerFormInput
                      dateTimeProps={{ disabled: isLoading }}
                      validationFailMessage="Needs to be before event end, and equal to or after doors open."
                      deps={["end_at", "doors_at"]}
                      validate={(val, otherValues) => {
                        return (
                          val.isBefore(otherValues.end_at) &&
                          (val.isSame(otherValues.doors_at) ||
                            val.isAfter(otherValues.doors_at))
                        );
                      }}
                      name="start_at"
                      label="Event start"
                      timezone={kydEvent.timezone}
                    />
                    <DatepickerFormInput
                      dateTimeProps={{ disabled: isLoading }}
                      validationFailMessage="Needs to be after off sale."
                      deps={["off_sale_at"]}
                      validate={(val, otherValues) => {
                        return (
                          val.isSame(otherValues.off_sale_at) ||
                          val.isAfter(otherValues.off_sale_at)
                        );
                      }}
                      name="end_at"
                      label="Event end"
                      timezone={kydEvent.timezone}
                    />
                  </Stack>
                </VStack>
              </Stack>
            </FormProvider>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button isLoading={isLoading} onClick={onCloseModal} mr="4">
                Cancel
              </Button>
              <Button
                isLoading={isLoading}
                color="white"
                bg="green.500"
                isDisabled={!isValid || !isFormDirty}
                type="submit"
              >
                Save
              </Button>
            </HStack>
          </ModalFooter>
        </KydForm>
      </ModalContent>
    </Modal>
  );
};

export default EditTimelineModal;
